import React from 'react';
import PropTypes from 'prop-types';
import $ from 'jquery';
import 'devbridge-autocomplete';
import axios from 'axios';
import api from '../../../general/js/api';

export default class PredictiveSearch extends React.PureComponent {
    constructor(props) {
        super(props);

        this.onSubmit = this.onSubmit.bind(this);
        this.onClearButtonClick = this.onClearButtonClick.bind(this);
        this.inputRef = this.inputRef.bind(this);
        this.formRef = this.formRef.bind(this);

        this.baseElement = null;
        this.inputElement = null;
        this.lastRequestCancel = null;
        this.predictiveSearchUrl = this.props.endpointUrl;
        this.maxSuggestionsCount = this.props.maxSuggestionsCount;
        this.moreSuggestionsAvailableClass = 'more-suggestions-available';
    }

    componentWillUnmount() {
        this.autocompleteInstance.dispose();
    }

    transformSuggestions(result) {
        return {
            suggestions: result.map((suggestion) => {
                const {
                    text, searchType, ...rest
                } = suggestion;
                return {
                    value: text,
                    data: {
                        type: searchType.toString(),
                        ...rest,
                    }
                };
            })
        };
    }

    componentDidMount() {
        this.$inputElement = $(this.inputElement);

        this.autocompleteInstance = this.$inputElement.devbridgeAutocomplete({
            minChars: 3,
            deferRequestBy: 300,
            zIndex: 30,
            appendTo: this.baseElement,
            showNoSuggestionNotice: true,
            noSuggestionNotice: 'No results found!',
            triggerSelectOnValidInput: false,
            lookup: (query, done) => {
                this.spinnerElement.classList.add('is-loading');
                this.getSuggestions(query).then((data) => {
                    if (document.activeElement === this.inputElement) {
                        if (data.total > this.maxSuggestionsCount) {
                            this.baseElement.classList.add(this.moreSuggestionsAvailableClass);
                        } else {
                            this.baseElement.classList.remove(this.moreSuggestionsAvailableClass);
                        }
                        done(this.transformSuggestions(data.result));
                    }
                    this.spinnerElement.classList.remove('is-loading');
                }, (error) => {
                    // hide after error only when our request has been canceled
                    if (!axios.isCancel(error)) {
                        this.spinnerElement.classList.remove('is-loading');
                    }
                });
            },
            /**
             * Build suggestion markup
             * Based on default library method
             *
             * @param suggestion
             * @param currentValue
             * @returns {string}
             */
            formatResult: (suggestion, currentValue) => {
                let highlightedText = suggestion.value;

                // Do not replace anything if the current value is empty
                if (currentValue) {
                    const pattern = '(' + currentValue.replace(/[|\\{}()[\]^$+*?.]/g, '\\$&') + ')';
                    highlightedText = highlightedText.replace(new RegExp(pattern, 'gi'), '<strong>$1</strong>');
                }

                return this.props.renderSuggestion ?
                    this.props.renderSuggestion(suggestion.value, suggestion.data.type, suggestion.data, highlightedText) :
                    highlightedText;
            },
            onSelect:
                (suggestion) => {
                    this.handlePredictiveSelect(suggestion);
                }
        }).devbridgeAutocomplete();
        this.autocompleteInstance.suggestionsContainer.setAttribute('max-suggestions-count', this.maxSuggestionsCount);
    }

    getSuggestions(query) {
        if (this.lastRequestCancel) {
            this.lastRequestCancel();
            this.lastRequestCancel = null;
        }
        const CancelToken = axios.CancelToken;
        const source = CancelToken.source();
        this.lastRequestCancel = source.cancel.bind(source);

        return api.get(this.predictiveSearchUrl, {
            params: { term: query },
            cancelToken: source.token
        }).then(
            ({ data }) => data,
            (error) => {
                if (!axios.isCancel(error)) {
                    this.lastRequestCancel = null;
                }
                return Promise.reject(error);
            }
        );
    }

    formRef(el) {
        this.baseElement = el;
    }

    inputRef(el) {
        this.inputElement = el;
        if (this.props.inputRef) {
            this.props.inputRef(el);
        }
    }

    isFilledMode() {
        return this.props.query !== '' && this.props.isSubmitted && !this.props.isFocused;
    }

    renderInnerButton() {
        if (this.isFilledMode()) {
            return (
                <button onClick={this.onClearButtonClick} type="button"
                    className="predictive-search__inner-button"
                    aria-label="Clear query">
                    <span className="button-content">
                        <svg className="icon" width="10" height="10">
                            <use xlinkHref="#icon-cross"></use>
                        </svg>
                    </span>
                </button>
            );
        }

        return (
            <button onClick={this.props.onSearchButtonClick} type="submit"
                className="predictive-search__inner-button"
                aria-label="Search">
                <span className="button-content">
                    <svg className="icon" width="17" height="17">
                        <use xlinkHref="#icon-search"></use>
                    </svg>
                </span>
            </button>
        );
    }

    render() {
        const isFilledMode = this.isFilledMode();
        const isMinimized = this.props.isMinimized;
        const isExpanded = this.props.isExpanded && !isFilledMode;

        return (
            <div
                className={`predictive-search ${(isMinimized && !isExpanded) ? 'is-minimized' : ''} ${isFilledMode ? 'is-filled' : ''}`}>
                <form className="predictive-search__inner" ref={this.formRef}
                    onSubmit={this.onSubmit}>
                    <input onChange={this.props.onChange}
                        value={this.props.query}
                        onFocus={this.props.onFocus}
                        onBlur={this.props.onBlur}
                        ref={this.inputRef}
                        placeholder="Player name or nationality"
                        className="predictive-search__input" type="text"/>

                    {/* additional input is a workaround to stop form submit after clicking on button[type="button"] */}
                    <input type="hidden"/>
                    <div className="predictive-search__spinner spinner spinner--blue"
                        ref={(el) => { this.spinnerElement = el; }}>
                    </div>
                    {this.renderInnerButton(isFilledMode)}
                </form>
                {
                    (isMinimized && isExpanded) ? (
                        <button onClick={this.props.onCollapseButtonClick} type="button"
                            className="predictive-search__collapse-button">
                            <span className="button-content">
                                <svg className="icon" width="10" height="10">
                                    <use xlinkHref="#icon-cross"></use>
                                </svg>
                            </span>
                        </button>
                    ) : null
                }
            </div>
        );
    }

    onClearButtonClick(e) {
        e.preventDefault();
        this.props.onClear();
    }

    onSubmit(e) {
        e.preventDefault();
        this.props.onSubmit();
    }

    handlePredictiveSelect(suggestion) {
        this.props.onSelect(suggestion.value, suggestion.data.type, suggestion.data);
    }
}

PredictiveSearch.propTypes = {
    endpointUrl: PropTypes.string.isRequired,
    maxSuggestionsCount: PropTypes.number.isRequired,
    renderSuggestion: PropTypes.func,
    onClear: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    query: PropTypes.string,
    onBlur: PropTypes.func.isRequired,
    onFocus: PropTypes.func.isRequired,
    onCollapseButtonClick: PropTypes.func.isRequired,
    onSearchButtonClick: PropTypes.func.isRequired,
    inputRef: PropTypes.func,
    isMinimized: PropTypes.bool.isRequired,
    isSubmitted: PropTypes.bool.isRequired,
    isFocused: PropTypes.bool.isRequired,
    isExpanded: PropTypes.bool.isRequired,
};
